import React, { useState } from 'react';
import { Search, X, User as UserIcon, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useContactSearch } from '@/hooks/useContactSearch';

interface Props {
  value: { id: string; name: string } | null;
  onChange: (value: { id: string; name: string } | null) => void;
  placeholder?: string;
}

/**
 * Seletor inline de cliente para filtros: input com busca + lista de resultados.
 * Usa o RPC search_contacts_unaccent via useContactSearch.
 */
export function ContactPicker({ value, onChange, placeholder = 'Buscar cliente...' }: Props) {
  const [term, setTerm] = useState('');
  const { contacts, loading } = useContactSearch(term, 10);

  if (value) {
    return (
      <Badge variant="secondary" className="h-9 px-3 rounded-full gap-2 inline-flex items-center w-fit max-w-full">
        <UserIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{value.name}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5 flex-shrink-0"
          aria-label="Remover cliente"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </Badge>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={placeholder}
        className="pl-9 h-9 rounded-full"
      />
      {term.trim().length >= 1 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-popover shadow-md">
          {loading ? (
            <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Buscando...
            </div>
          ) : contacts.length === 0 ? (
            <div className="py-3 px-3 text-sm text-muted-foreground">Nenhum cliente encontrado.</div>
          ) : (
            contacts.map((c: any) => {
              const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.primary_email || '—';
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange({ id: c.id, name });
                    setTerm('');
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2"
                >
                  <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
